/**
 * slideComponents.ts
 *
 * Catalogue of all slide component types available in the pseudo-PPTX editor.
 *
 * Each entry declares:
 *  - `family`         — Visual family grouping used in the "Agregar diapositiva" dropdown.
 *  - `label`          — Spanish display name shown in the UI.
 *  - `requiredFields` — Fields that must have non-empty values for the slide to be valid.
 *
 * `SLIDE_COMPONENT_FAMILIES` is the ordered family registry (display label per family key).
 * `SLIDE_COMPONENTS` maps a component-type string to its metadata.
 * `SlideComponentType` is the union of all valid component-type keys.
 */

// ─── Family registry ──────────────────────────────────────────────────────────

/**
 * All visual families and their Spanish labels.
 * Order here determines the order in grouped dropdowns.
 */
export const SLIDE_COMPONENT_FAMILIES = {
  hero: 'Cover & Sections',
  content: 'Content',
  product: 'Products',
  showcase: 'Showcase',
  data: 'Data',
  people: 'People',
  journey: 'Journey',
} as const;

/** Union of all family keys. */
export type SlideComponentFamily = keyof typeof SLIDE_COMPONENT_FAMILIES;

// ─── Component catalogue ──────────────────────────────────────────────────────

/**
 * Full catalogue of slide component types.
 * Keys must match the `component` field values stored in `SlideConfig.component`.
 */
export const SLIDE_COMPONENTS = {
  // ── Hero family ─────────────────────────────────────────────────────────────
  hero_cover: {
    family: 'hero' as SlideComponentFamily,
    label: 'Cover',
    requiredFields: ['title', 'subtitle'],
    needsImage: false,
  },
  hero_stats: {
    family: 'hero' as SlideComponentFamily,
    label: 'Statistics',
    requiredFields: ['stats'],
    needsImage: false,
  },
  hero_section: {
    family: 'hero' as SlideComponentFamily,
    label: 'Section Divider',
    requiredFields: ['title'],
    needsImage: false,
  },
  hero_closing: {
    family: 'hero' as SlideComponentFamily,
    label: 'Closing',
    requiredFields: ['title'],
    needsImage: false,
  },
  hero_client_logos: {
    family: 'hero' as SlideComponentFamily,
    label: 'Client Logos',
    requiredFields: [] as string[],
    needsImage: false,
  },

  // ── Content family ───────────────────────────────────────────────────────────
  concepto_full: {
    family: 'content' as SlideComponentFamily,
    label: 'Concept',
    requiredFields: ['title'],
    needsImage: false,
  },
  content_image_right: {
    family: 'content' as SlideComponentFamily,
    label: 'Content + Image Right',
    requiredFields: ['title'],
    needsImage: true,
  },
  content_image_left: {
    family: 'content' as SlideComponentFamily,
    label: 'Content + Image Left',
    requiredFields: ['title'],
    needsImage: true,
  },

  // ── Journey family ───────────────────────────────────────────────────────────
  journey_overview: {
    family: 'journey' as SlideComponentFamily,
    label: 'Journey',
    requiredFields: ['journey_items'],
    needsImage: false,
  },

  // ── Data family ──────────────────────────────────────────────────────────────
  data_timeline: {
    family: 'data' as SlideComponentFamily,
    label: 'Minute by Minute',
    requiredFields: ['timeline_items'],
    needsImage: false,
  },
  data_cost_table: {
    family: 'data' as SlideComponentFamily,
    label: 'Cost Table',
    requiredFields: ['headers', 'rows'],
    needsImage: false,
  },

  // ── Product family ───────────────────────────────────────────────────────────
  product_showcase_right: {
    family: 'product' as SlideComponentFamily,
    label: 'Product Right',
    requiredFields: ['title'],
    needsImage: true,
  },
  product_showcase_left: {
    family: 'product' as SlideComponentFamily,
    label: 'Product Left',
    requiredFields: ['title'],
    needsImage: true,
  },
  product_showcase_pair: {
    family: 'product' as SlideComponentFamily,
    label: 'Product Pair',
    requiredFields: [] as string[],
    needsImage: true,
  },
  product_grid_2: {
    family: 'product' as SlideComponentFamily,
    label: 'Product Grid 2',
    requiredFields: [] as string[],
    needsImage: true,
  },
  product_grid_3: {
    family: 'product' as SlideComponentFamily,
    label: 'Product Grid 3',
    requiredFields: [] as string[],
    needsImage: true,
  },

  // ── Showcase family ──────────────────────────────────────────────────────────
  showcase_full_bleed: {
    family: 'showcase' as SlideComponentFamily,
    label: 'Full Bleed',
    requiredFields: [] as string[],
    needsImage: true,
  },
  showcase_venue: {
    family: 'showcase' as SlideComponentFamily,
    label: 'Venue',
    requiredFields: [] as string[],
    needsImage: true,
  },
  zona_instagrameable_showcase: {
    family: 'showcase' as SlideComponentFamily,
    label: 'Instagram Zone',
    requiredFields: [] as string[],
    needsImage: true,
  },
  showcase_image_right_round: {
    family: 'showcase' as SlideComponentFamily,
    label: 'Round Image Right',
    requiredFields: ['title'],
    needsImage: true,
  },
  showcase_image_left_round: {
    family: 'showcase' as SlideComponentFamily,
    label: 'Round Image Left',
    requiredFields: ['title'],
    needsImage: true,
  },
  invitation_showcase: {
    family: 'showcase' as SlideComponentFamily,
    label: 'Invitation',
    requiredFields: [] as string[],
    needsImage: true,
  },
  render_showcase: {
    family: 'showcase' as SlideComponentFamily,
    label: 'Render',
    requiredFields: [] as string[],
    needsImage: true,
  },

  // ── People family ────────────────────────────────────────────────────────────
  people_composite: {
    family: 'people' as SlideComponentFamily,
    label: 'Team',
    requiredFields: [] as string[],
    needsImage: true,
  },
  people_speaker_bio: {
    family: 'people' as SlideComponentFamily,
    label: 'Speaker Bio',
    requiredFields: ['title'],
    needsImage: true,
  },
} as const;

/** Union of all valid component-type keys (matches `SlideConfig.component`). */
export type SlideComponentType = keyof typeof SLIDE_COMPONENTS;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns the display label for a component type, or the raw key if unknown.
 *
 * @param component - Value of `SlideConfig.component`.
 * @returns Spanish label string.
 */
export function getComponentLabel(component: string): string {
  if (component in SLIDE_COMPONENTS) {
    return SLIDE_COMPONENTS[component as SlideComponentType].label;
  }
  return component;
}

/**
 * Returns the family key for a component type, or `'content'` as fallback.
 *
 * @param component - Value of `SlideConfig.component`.
 * @returns Family key string.
 */
export function getComponentFamily(component: string): SlideComponentFamily {
  if (component in SLIDE_COMPONENTS) {
    return SLIDE_COMPONENTS[component as SlideComponentType].family;
  }
  return 'content';
}

/**
 * Whether a component type requires at least one generated image.
 * Used by the ImageGenerationStep to decide which slides to show
 * image controls for, independent of `slide.images[]` being populated.
 */
export function componentNeedsImage(component: string): boolean {
  if (component in SLIDE_COMPONENTS) {
    return SLIDE_COMPONENTS[component as SlideComponentType].needsImage;
  }
  return true;
}

/**
 * How many image slots a component type needs.
 * Most need 1; pair/grid/invitation components need 2+.
 */
export function componentImageCount(component: string): number {
  switch (component) {
    case 'product_showcase_pair':
    case 'invitation_showcase':
      return 2;
    case 'product_grid_2':
      return 2;
    case 'product_grid_3':
      return 3;
    default:
      return 1;
  }
}
